import { Close } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  styled
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { asyncAutocompleteStyles, inputBaseStyles, requiredAsteriskStyles } from '../../styles/inputStyles';
import CloseIcon from './icons/CloseIcon';
import TextField from './TextField';

// Define the option type
export type Option = Record<string, string | number | boolean | undefined> & { value: string; label: string };

interface AsyncMultiAutocompleteProps {
  label?: string;
  placeholder?: string;
  value: Option | Option[] | null;
  onChange: (value: Option | Option[] | null) => void;
  loadOptions: (searchQuery: string, otherQuery?: string) => Promise<Option[]>;
  width?: string;
  debounceMs?: number;
  otherQuery?: string;
  multiple?: boolean;
  className?: string;
  error?: boolean;
  helperText?: string;
}

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  ...inputBaseStyles,
  ...requiredAsteriskStyles,
  ...asyncAutocompleteStyles,
}));

export const AsyncMultiAutocomplete: React.FC<AsyncMultiAutocompleteProps> = ({
  label,
  placeholder = '',
  value,
  onChange,
  loadOptions,
  width = '100%',
  debounceMs = 300,
  otherQuery = '',
  multiple = false,
  className = '',
  error = false,
  helperText = '',
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputValue);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue, debounceMs]);

  // Fetch options when search query changes
  useEffect(() => {
    let active = true;

    if (!open || options.length > 0) {
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const results = await loadOptions(searchQuery, otherQuery);
        if (active) {
          setOptions(results);
        }
      } catch (error) {
        console.error('Error loading options:', error);
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [open, searchQuery, loadOptions, otherQuery, options.length]);

  const handleInputChange = useCallback((_: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  return (
    <StyledAutocomplete
      multiple={multiple}
      className={className}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setLoading(false);
        setOpen(false);
      }}
      disableCloseOnSelect
      defaultValue={multiple ? value : undefined}
      value={multiple ? undefined : (value as Option)}
      onChange={(_, newValue) => onChange(newValue as any)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={loading}
      getOptionLabel={(option) => (option as Option)?.label || ''}
      isOptionEqualToValue={(option, value) => (option as Option)?.value === (value as Option)?.value}
      popupIcon={<KeyboardArrowDownIcon sx={{ width: '16px', height: '16px' }} />}
      clearIcon={<CloseIcon sx={{ width: '16px', height: '16px' }} />}
      slotProps={{
        listbox: {
          sx: {
            '& .MuiAutocomplete-option': {
              fontSize: '15px !important',
              fontWeight: 400,
              fontFamily: 'Inter',
              padding: '8px 14px',
              color: '#000000 !important'
            }
          }
        },
      }}
      renderTags={(value, getTagProps) =>
        (value as Option[]).map((option, index) => (
          <Chip
            label={option.label}
            {...getTagProps({ index })}
            title={option.label}
            sx={{
              background: '#FDE7E3',
              color: '#E95845',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              borderRadius: '4px !important',
              fontSize: '15px',
              fontWeight: 500,
              fontFamily: 'Inter',
            }}
            deleteIcon={<Close style={{ fontSize: '15px', color: 'black' }} />}
          />
        ))
      }
      sx={{
        ...(multiple && {
          '& .MuiAutocomplete-inputRoot': {
            flexWrap: 'wrap',
            height: 'auto !important',
          }
        }),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          width={width}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};